import React from "react";
import { Box } from "@mui/joy";
import LeaveRequestsList from "./display/LeaveRequestsList";
import LeaveRequestFormModal from "./display/LeaveRequestFormModal";
import LeaveManagementBalancesCard from "./display/LeaveManagementBalancesCard";
import { useLeave } from "../../contexts/api";
import { useAuth } from "../../contexts/api";
import { useHRData } from "../../contexts/app/hrData";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import { findGlobalUnavailabilityLeaveType } from "../../utils/planningLeaveHelpers";
import type { LeaveType, UserLeaveTypeBalance } from "../../api/leave/Leave.typed";
import CommentsModal from "../common/CommentsModal";
import type { MultiComment } from "../../api/zekay/comments";
import DarkTypography from "../common/DarkTypography";
import { NO_ACCESS_MESSAGE } from "../common/DashboardPage";
import { userDisplayName } from "../../utils/userHelper";

export default function LeaveManagementComponent() {
  const { listBalancesByUser, setLeaveTypeBalance } = useLeave();
  const { user } = useAuth();
  const { leaveTypes: leaveTypesAll } = useHRData();
  const { hasPermission } = usePermissions();
  const canAccessLeaveManagement =
    hasPermission(Permission.LEAVE_ACCESS) && hasPermission(Permission.LEAVE_CREATE);
  const globalUnavailabilityLeaveType = React.useMemo(
    () => findGlobalUnavailabilityLeaveType(leaveTypesAll),
    [leaveTypesAll],
  );
  const viewerNonEmployee = user?.Role?.is_employee === false;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [allowedTypeIds, setAllowedTypeIds] = React.useState<number[]>([]);
  const [userBalances, setUserBalances] = React.useState<UserLeaveTypeBalance[]>([]);
  const [openCommentsTypeId, setOpenCommentsTypeId] = React.useState<number | null>(null);
  const [openComments, setOpenComments] = React.useState<MultiComment[] | undefined>(undefined);

  const typesById = React.useMemo(() => {
    const m: Record<number, LeaveType> = {};
    for (const t of leaveTypesAll) m[t.ID] = t;
    return m;
  }, [leaveTypesAll]);

  React.useEffect(() => {
    const loadAllowed = async () => {
      if (!canAccessLeaveManagement || !user?.id) {
        setAllowedTypeIds([]);
        setUserBalances([]);
        return;
      }
      const balancesPaged = await listBalancesByUser(user.id, 1, 1000);
      const balances = balancesPaged?.data || [];
      setUserBalances(balances);
      const hasByType = new Set(balances.map((b) => b.TypeID));
      if (user?.Role?.is_employee === false && globalUnavailabilityLeaveType) {
        setAllowedTypeIds([globalUnavailabilityLeaveType.ID]);
      } else {
        const fromBalances = leaveTypesAll.filter((t) => hasByType.has(t.ID)).map((t) => t.ID);
        const ids = new Set(fromBalances);
        if (globalUnavailabilityLeaveType?.IsActive) {
          ids.add(globalUnavailabilityLeaveType.ID);
        }
        setAllowedTypeIds(Array.from(ids));
      }
    };
    loadAllowed();
  }, [
    canAccessLeaveManagement,
    leaveTypesAll,
    listBalancesByUser,
    user?.id,
    user?.Role?.is_employee,
    globalUnavailabilityLeaveType,
    refreshTrigger,
  ]);

  React.useEffect(() => {
    if (openCommentsTypeId == null) {
      setOpenComments(undefined);
      return;
    }
    const b = userBalances.find((x) => x.TypeID === openCommentsTypeId);
    setOpenComments(b?.MultiComments || []);
  }, [openCommentsTypeId, userBalances]);

  const handleNewRequest = React.useCallback(() => {
    if (allowedTypeIds.length === 0) return;
    setModalOpen(true);
  }, [allowedTypeIds]);

  React.useEffect(() => {
    if (modalOpen && allowedTypeIds.length === 0) setModalOpen(false);
  }, [allowedTypeIds, modalOpen]);

  const handleModalSave = React.useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
    setModalOpen(false);
  }, []);

  const handleCommentsSave = React.useCallback(
    async (updated: MultiComment[] | undefined) => {
      if (!user?.id || openCommentsTypeId == null) return;
      await setLeaveTypeBalance(openCommentsTypeId, user.id, {
        MultiComments: updated || [],
      });
      const paged = await listBalancesByUser(user.id, 1, 1000);
      setUserBalances(paged?.data || []);
    },
    [listBalancesByUser, openCommentsTypeId, setLeaveTypeBalance, user?.id],
  );

  const canCreateRequest = canAccessLeaveManagement;

  if (!canAccessLeaveManagement) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: "50vh",
          p: 3,
        }}
      >
        <DarkTypography
          level="body-lg"
          colorVariant="muted"
          sx={{ textAlign: "center", maxWidth: 400 }}
        >
          {NO_ACCESS_MESSAGE}
        </DarkTypography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      {user?.id && (
        <LeaveManagementBalancesCard
          userBalances={userBalances}
          typesById={typesById}
          onOpenComments={setOpenCommentsTypeId}
        />
      )}
      <CommentsModal
        open={openCommentsTypeId != null}
        title="Commentaires"
        currentUserName={user ? userDisplayName(user) : ""}
        multiComments={openComments}
        setMultiComments={setOpenComments}
        onClose={() => setOpenCommentsTypeId(null)}
        onSave={handleCommentsSave}
      />
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <LeaveRequestsList
          showMyRequestsOnly={true}
          key={refreshTrigger}
          onNewRequest={canCreateRequest ? handleNewRequest : undefined}
          newRequestDisabled={allowedTypeIds.length === 0}
        />
      </Box>
      <LeaveRequestFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
        allowedTypeIds={allowedTypeIds}
        restrictTypeSelectionToAllowedOnly={
          viewerNonEmployee && globalUnavailabilityLeaveType != null
        }
      />
    </Box>
  );
}
